/**
 * Rows that open (ROW-PATTERN-001 step 2, ported from parkify-demo #106
 * `TRowOpen`, merge 023b4c6; the action-cell isolation is #103, 4cbdc2c).
 *
 * `RowOpen` is the row's PRIMARY CONTROL: a real button inside the primary
 * cell, holding the text the cell already showed, named
 * "<visible text>, <action>" (e.g. "Mercer Dental Group, open tenant
 * details"). The visible text comes first so the accessible name contains it
 * (WCAG 2.5.3). The ROW stays the pointer target and keeps its own onClick;
 * this button is the keyboard target and stops propagation, so a click or an
 * Enter on it opens the destination once, not once for the button and again
 * for the row.
 *
 * It sets no colour or weight of its own (everything inherits), so a row keeps
 * exactly the look it had.
 *
 * `RowActions` is the action cell's INTERACTION BOUNDARY: nothing inside it
 * (click or key) reaches the row. Without it, Enter on a Remove button inside
 * a row with a keyboard handler bubbled up and opened the row instead.
 */
import type { CSSProperties, ReactNode } from 'react'

interface RowOpenProps {
  /** The visible text of the primary cell, first in the accessible name. */
  text: string
  /** Where the control goes, e.g. "open tenant details" or "edit parker". */
  action: string
  onOpen: () => void
  /** Set for a row that expands in place rather than opening a detail. */
  expanded?: boolean
  children: ReactNode
  style?: CSSProperties
}

const resetStyle: CSSProperties = {
  background: 'none', border: 'none', padding: 0, margin: 0,
  font: 'inherit', color: 'inherit', fontWeight: 'inherit', lineHeight: 'inherit',
  letterSpacing: 'inherit', textAlign: 'left', cursor: 'pointer',
  display: 'block', maxWidth: '100%', minWidth: 0,
}

export function RowOpen({ text, action, onOpen, expanded, children, style }: RowOpenProps) {
  return (
    <button
      type="button"
      aria-label={`${text}, ${action}`}
      aria-expanded={expanded}
      onClick={e => { e.stopPropagation(); onOpen() }}
      // The activation keys are this button's own: a row that also listens
      // for Enter must not open a second time from the same key press.
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation() }}
      style={{ ...resetStyle, ...style }}
    >
      {children}
    </button>
  )
}

interface RowActionsProps {
  children: ReactNode
  style?: CSSProperties
  /** Render as a table cell for <tr> rows. */
  as?: 'div' | 'td'
}

export function RowActions({ children, style, as = 'div' }: RowActionsProps) {
  const Tag = as
  return (
    <Tag
      style={style}
      onClick={e => e.stopPropagation()}
      onKeyDown={e => e.stopPropagation()}
    >
      {children}
    </Tag>
  )
}

/**
 * The row's navigation cue (demo #106 `TRowCue`): a decorative `›` in a
 * trailing cell of a row that opens. aria-hidden, because the primary control
 * already names the destination. Rest colour #6B6B6B, over 3:1 on white (the
 * demo moved it off a ~1.3:1 border tone for exactly that reason).
 */
export function RowCue() {
  return (
    <span aria-hidden="true" data-row-cue="" style={{ display: 'inline-flex', color: '#6B6B6B' }}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
    </span>
  )
}
